import React, { useEffect, useState, useRef } from "react";
import { api, API } from "@/lib/config";
import { Plus, Edit2, Trash2, X, Save, Upload } from "lucide-react";

const EMPTY = {
  slug: "", title: "", excerpt: "", content: "", cover_image: "",
  tags: "", published: true,
};

function formatErr(d) {
  if (!d) return "Erreur";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

function slugify(text) {
  if (!text) return "";
  return text
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get("/admin/blog").then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, []);

  const onEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p, tags: (p.tags || []).join(", ") });
    setErr("");
  };

  const onNew = () => { setEditing("new"); setForm(EMPTY); setErr(""); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Use full URL so it works in production too
      const fullUrl = data.url.startsWith("http") ? data.url : `${API.replace(/\/api$/, "")}${data.url}`;
      setForm(f => ({ ...f, cover_image: fullUrl }));
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Erreur de l'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onSave = async () => {
    setErr("");
    const payload = { ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) };
    try {
      if (editing === "new") {
        await api.post("/admin/blog", payload);
      } else {
        await api.put(`/admin/blog/${editing}`, payload);
      }
      setEditing(null); load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/admin/blog/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <p className="section-num">Articles</p>
          <h1 className="display text-4xl mt-3">Blog</h1>
        </div>
        <button onClick={onNew} className="btn-pill btn-primary" data-testid="admin-new-post">
          <Plus size={16}/> Nouvel article
        </button>
      </div>

      <div className="mt-12 bg-paper border border-ink/10">
        {posts.length === 0 && <p className="p-8 text-ink/60">Aucun article.</p>}
        {posts.map(p => (
          <div key={p.id} className="flex items-center justify-between p-5 border-b border-ink/10 last:border-b-0" data-testid={`admin-post-${p.id}`}>
            <div className="flex-1 min-w-0">
              <p className="display text-lg truncate">{p.title}</p>
              <p className="text-sm text-ink/60 truncate">/{p.slug} · {p.published ? "Publié" : "Brouillon"} · {(p.tags || []).join(", ")}</p>
            </div>
            <div className="flex gap-2 ml-4">
              <button onClick={() => onEdit(p)} className="p-2 hover:bg-ink/5"><Edit2 size={16}/></button>
              <button onClick={() => onDelete(p.id)} className="p-2 hover:bg-paper-2 text-accent-pink"><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-paper max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 border-b border-ink/10 flex items-center justify-between">
              <h2 className="display text-2xl">{editing === "new" ? "Nouvel article" : "Modifier l'article"}</h2>
              <button onClick={() => setEditing(null)}><X size={22}/></button>
            </div>
            <div className="p-8 space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Titre</label>
                <input
                  className="field"
                  value={form.title}
                  onChange={e => {
                    const newTitle = e.target.value;
                    setForm(f => ({
                      ...f,
                      title: newTitle,
                      // Auto-fill slug from title only if user hasn't typed a custom one yet
                      slug: (!f.slug || f.slug === slugify(f.title)) ? slugify(newTitle) : f.slug,
                    }));
                  }}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Slug (URL)</label>
                <input className="field" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.replace(/[^a-z0-9-]/gi, '-').toLowerCase() }))}/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Extrait (résumé court)</label>
                <textarea className="field" rows={3} value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Contenu (paragraphes séparés par une ligne vide)</label>
                <textarea className="field" rows={12} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Infographie / Image de couverture</label>
                {form.cover_image && (
                  <div className="mt-3 relative inline-block">
                    <img
                      src={form.cover_image}
                      alt="cover"
                      className="max-h-48 max-w-full border border-ink/10"
                      data-testid="cover-preview"
                    />
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, cover_image: "" }))}
                      className="absolute top-2 right-2 p-1.5 bg-paper rounded-full shadow border border-ink/10 hover:bg-paper-2"
                      data-testid="remove-cover"
                      title="Retirer l'image"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                <div className="flex gap-3 mt-3 items-center flex-wrap">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={onUpload}
                    className="hidden"
                    data-testid="upload-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="btn-pill btn-ghost disabled:opacity-60"
                    data-testid="upload-btn"
                  >
                    {uploading ? "Envoi…" : (<><Upload size={14}/> {form.cover_image ? "Remplacer l'image" : "Uploader une image"}</>)}
                  </button>
                  <span className="text-xs text-ink/50">ou collez une URL :</span>
                </div>
                <input
                  className="field mt-2"
                  placeholder="https://…"
                  value={form.cover_image}
                  onChange={e => setForm(f => ({ ...f, cover_image: e.target.value }))}
                  data-testid="cover-url-input"
                />
                <p className="text-[11px] text-ink/40 mt-2">JPG, PNG, WebP, GIF ou SVG. 8 Mo max.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Tags (séparés par des virgules)</label>
                <input className="field" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}/>
              </div>
              <label className="flex items-center gap-3 pt-3">
                <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}/>
                <span className="text-sm">Publié (visible sur le site)</span>
              </label>
              {err && <p className="text-accent-pink text-sm">{err}</p>}
              <div className="flex gap-3 pt-4">
                <button onClick={onSave} className="btn-pill btn-primary" data-testid="admin-save-post"><Save size={16}/> Enregistrer</button>
                <button onClick={() => setEditing(null)} className="btn-pill btn-ghost">Annuler</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
